import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildCustomerRoute, isSubdomainStoreMode, resolveCustomerStoreHomePath } from '../../utils/storeUtils';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const resolveCustomerDestination = (route) => {
  const pathname = window.location.pathname;
  const storedStoreId = localStorage.getItem('currentStoreId');
  if (pathname.includes('/set-password')) {
    return resolveCustomerStoreHomePath({
      pathname,
      queryStoreId: new URLSearchParams(window.location.search).get('storeId'),
      storedStoreId,
    });
  }

  return storedStoreId ? (buildCustomerRoute(storedStoreId, route) || '/not-found') : '/not-found';
};

export default function RedirectIfAuthenticated({ children, redirectTo }) {
  const { isAuthenticated, user, loading } = useAuth();
  
  // Check localStorage directly as a fallback (path-aware for dual auth)
  const isCustomerPage = isSubdomainStoreMode() || window.location.pathname.startsWith('/stores/');
  const hasToken = localStorage.getItem(isCustomerPage ? 'customer_auth_token' : 'management_auth_token');
  const hasUserData = localStorage.getItem(isCustomerPage ? 'customer_user_data' : 'management_user_data');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Redirect if authenticated OR if we have valid tokens in localStorage
  if (isAuthenticated || (hasToken && hasUserData)) {
    // Determine redirect based on user type if redirectTo not specified
    let targetRedirect = redirectTo;
    
    if (!targetRedirect) {
      // Try to get user type from context or localStorage
      const userType = user?.userType;
      const userTypeNumber = typeof userType === 'number' 
        ? userType 
        : (userType === 'Customer' ? 3 : userType === 'AppAdmin' ? 1 : userType === 'TeamMember' ? 2 : null);
      
      // If we have user data in localStorage but not in context yet, try to parse it
      if (!userTypeNumber && hasUserData) {
        try {
          const parsedUser = JSON.parse(hasUserData);
          const parsedType = typeof parsedUser.userType === 'number' 
            ? parsedUser.userType 
            : (parsedUser.userType === 'Customer' ? 3 : parsedUser.userType === 'AppAdmin' ? 1 : parsedUser.userType === 'TeamMember' ? 2 : null);
          
          if (parsedType === 3) {
            targetRedirect = resolveCustomerDestination('');
          } else {
            targetRedirect = '/management';
          }
        } catch (e) {
          targetRedirect = '/management';
        }
      } else if (userTypeNumber === 3) {
        targetRedirect = resolveCustomerDestination('my-appointments');
      } else {
        targetRedirect = '/management';
      }
    } else if (targetRedirect && targetRedirect.includes('/stores/')) {
      // If redirectTo already has storeId, use it
      // No changes needed
    }
    
    return <Navigate to={targetRedirect} replace />;
  }

  return children;
}
